import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Plus, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchBrief, generateDraft } from "@/lib/contentOps/api";
import type { ArticleRow, BriefStatus } from "@/lib/contentOps/types";
import EditorialTools from "../EditorialTools";

interface Props {
  article: ArticleRow;
  onArticleChange: (a: ArticleRow) => void;
  onSavePatch: (p: Partial<ArticleRow>) => Promise<void>;
}

const DraftTab = ({ article, onArticleChange, onSavePatch }: Props) => {
  const [generating, setGenerating] = useState(false);
  const [local, setLocal] = useState(article);
  const [briefStatus, setBriefStatus] = useState<BriefStatus | null>(null);
  const [briefLoading, setBriefLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBriefLoading(true);
    fetchBrief(article.id)
      .then((b) => { if (!cancelled) setBriefStatus((b?.status as BriefStatus) ?? null); })
      .finally(() => { if (!cancelled) setBriefLoading(false); });
    return () => { cancelled = true; };
  }, [article.id]);

  const sync = (p: Partial<ArticleRow>) => {
    const next = { ...local, ...p };
    setLocal(next);
    onArticleChange(next);
  };

  const jumpToBrief = () => {
    window.dispatchEvent(new CustomEvent("contentops:jump", { detail: { tab: "brief" } }));
  };

  const run = async () => {
    setGenerating(true);
    try {
      const res = await generateDraft(article.id, article.content?.length ? "regenerate" : "draft");
      toast.success("Draft generated", { description: `${res.model}${res.confidence ? ` · confidence ${Math.round(res.confidence * 100)}%` : ""}` });
      // Reload article from DB by triggering parent update with patched flag; simplest: window dispatch
      window.dispatchEvent(new CustomEvent("contentops:reload-article", { detail: { id: article.id } }));
    } catch (e) {
      toast.error("Draft generation failed", { description: (e as Error).message });
    } finally {
      setGenerating(false);
    }
  };

  const updateBlock = (idx: number, patch: Partial<ArticleRow["content"][number]>) => {
    const next = [...local.content];
    next[idx] = { ...next[idx], ...patch };
    sync({ content: next });
  };

  const gated = !briefLoading && briefStatus !== "approved";

  return (
    <div className="space-y-4">
      {gated && (
        <div className="rounded-xl border p-4 flex items-start gap-3"
          style={{ background: "hsl(38 92% 45% / 0.08)", borderColor: "hsl(38 92% 50% / 0.35)" }}>
          <Lock size={16} style={{ color: "hsl(38 92% 65%)" }} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: "hsl(38 92% 75%)" }}>
              Brief must be approved before drafting
            </div>
            <p className="text-xs mt-0.5" style={{ color: "hsl(38 92% 70%)" }}>
              {briefStatus
                ? `Current brief status: ${briefStatus.replace("_", " ")}. Open the Brief tab and approve the brief to unlock draft generation.`
                : "No brief found. Generate one on the Brief tab first."}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={jumpToBrief}
            className="border" style={{ borderColor: "hsl(38 92% 50% / 0.5)", color: "hsl(38 92% 75%)", background: "transparent" }}>
            Open Brief
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between rounded-xl border p-4"
        style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>AI draft</h3>
          <p className="text-xs mt-0.5" style={{ color: "hsl(0 0% 55%)" }}>
            Generates from the approved brief. Each run is saved as a revision.
          </p>
        </div>
        <Button onClick={run} disabled={generating || gated}
          style={{ background: gated ? "hsl(0 0% 14%)" : "hsl(207 68% 50%)", color: "white" }}>
          {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
          {article.content?.length ? "Regenerate" : "Generate draft"}
        </Button>
      </div>

      <EditorialTools article={local} onApplied={() => {
        window.dispatchEvent(new CustomEvent("contentops:reload-article", { detail: { id: article.id } }));
      }} />

      <div className="rounded-xl border p-5 space-y-4" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
        <div data-field="excerpt">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Excerpt</label>
          <Textarea value={local.excerpt ?? ""} onChange={(e) => sync({ excerpt: e.target.value })}
            onBlur={() => onSavePatch({ excerpt: local.excerpt })} rows={2}
            className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>Summary</label>
          <Textarea value={local.summary ?? ""} onChange={(e) => sync({ summary: e.target.value })}
            onBlur={() => onSavePatch({ summary: local.summary })} rows={2}
            className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        </div>

        <div data-field="key_takeaways" tabIndex={-1} className="outline-none rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium" style={{ color: "hsl(0 0% 60%)" }}>Key takeaways</label>
            <Button size="sm" variant="ghost" onClick={() => sync({ key_takeaways: [...(local.key_takeaways ?? []), ""] })}
              className="h-6 px-2 text-xs" style={{ color: "hsl(207 68% 60%)" }}>
              <Plus size={12} className="mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1.5">
            {(local.key_takeaways ?? []).map((k, i) => (
              <div key={i} className="flex gap-2">
                <Input value={k} onChange={(e) => {
                  const arr = [...(local.key_takeaways ?? [])];
                  arr[i] = e.target.value;
                  sync({ key_takeaways: arr });
                }} onBlur={() => onSavePatch({ key_takeaways: local.key_takeaways })}
                  className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
                <Button size="sm" variant="ghost" onClick={() => {
                  const arr = [...(local.key_takeaways ?? [])];
                  arr.splice(i, 1);
                  sync({ key_takeaways: arr });
                  onSavePatch({ key_takeaways: arr });
                }} className="h-9 w-9 p-0" style={{ color: "hsl(0 75% 60%)" }}><Trash2 size={12} /></Button>
              </div>
            ))}
          </div>
        </div>

        <div data-field="content" tabIndex={-1} className="outline-none rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium" style={{ color: "hsl(0 0% 60%)" }}>Body</label>
            <Button size="sm" variant="ghost" onClick={() => sync({ content: [...(local.content ?? []), { heading: "", level: "h2", paragraphs: [""] }] })}
              className="h-6 px-2 text-xs" style={{ color: "hsl(207 68% 60%)" }}>
              <Plus size={12} className="mr-1" /> Add section
            </Button>
          </div>
          <div className="space-y-3">
            {(local.content ?? []).map((b, idx) => (
              <div key={idx} className="rounded-lg border p-3 space-y-2"
                style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 16%)" }}>
                <div className="flex gap-2">
                  <select value={b.level} onChange={(e) => updateBlock(idx, { level: e.target.value })}
                    onBlur={() => onSavePatch({ content: local.content })}
                    className="rounded-md border px-2 py-1.5 text-xs"
                    style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}>
                    <option value="h2">H2</option>
                    <option value="h3">H3</option>
                  </select>
                  <Input value={b.heading} onChange={(e) => updateBlock(idx, { heading: e.target.value })}
                    onBlur={() => onSavePatch({ content: local.content })}
                    placeholder="Heading…"
                    className="flex-1 border text-sm font-medium" style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 92%)" }} />
                  <Button size="sm" variant="ghost" onClick={() => {
                    const next = [...local.content];
                    next.splice(idx, 1);
                    sync({ content: next });
                    onSavePatch({ content: next });
                  }} className="h-9 w-9 p-0" style={{ color: "hsl(0 75% 60%)" }}><Trash2 size={12} /></Button>
                </div>
                {b.paragraphs.map((p, pi) => (
                  <div key={pi} className="flex gap-2">
                    <Textarea value={p} onChange={(e) => {
                      const next = [...b.paragraphs];
                      next[pi] = e.target.value;
                      updateBlock(idx, { paragraphs: next });
                    }} onBlur={() => onSavePatch({ content: local.content })} rows={3}
                      className="border resize-none text-sm" style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
                    <Button size="sm" variant="ghost" onClick={() => {
                      const next = b.paragraphs.filter((_, j) => j !== pi);
                      updateBlock(idx, { paragraphs: next.length ? next : [""] });
                      onSavePatch({ content: local.content });
                    }} className="h-9 w-9 p-0" style={{ color: "hsl(0 75% 60%)" }}><Trash2 size={12} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" onClick={() => updateBlock(idx, { paragraphs: [...b.paragraphs, ""] })}
                  className="h-7 px-2 text-xs" style={{ color: "hsl(207 68% 60%)" }}>
                  <Plus size={11} className="mr-1" /> Paragraph
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftTab;
